import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  CompetitivenessAdjustmentInput,
  CompetitivenessLevel,
} from '@client/graphql/__generated__/types';
import { Form, Formik } from 'formik';
import { PencilIcon } from 'lucide-react';
import { Modal } from '~/common/components/Modal/Modal';
import { ModalTrigger } from '~/common/components/Modal/ModalTrigger';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { Select } from '~/services/document/common/Select';
import { CompetitivenessLabel } from './CompetitivenessLabel';
import { COMPETITIVENESS_ORDER } from './constants';

type AdjustCompetitivenessModalTriggerProps = {
  competitiveness: CompetitivenessAdjustmentInput;
  onSubmit: (values: CompetitivenessAdjustmentInput) => Promise<void>;
};

export const AdjustCompetitivenessModalTrigger = ({
  competitiveness,
  onSubmit,
}: AdjustCompetitivenessModalTriggerProps) => {
  const isMobile = useIsMobile();

  return (
    <ModalTrigger
      modal={AdjustCompetitivenessModal}
      modalProps={{
        initialCompetitiveness: competitiveness,
        onSubmit,
      }}
    >
      {isMobile && (
        <Flex alignItems="center" gap={1}>
          <CompetitivenessLabel level={competitiveness.adjustedLevel} />
          <Icon as={PencilIcon} boxSize={3} color="white" ml={1} />
        </Flex>
      )}

      {!isMobile && (
        <Box
          className="edit-icon-container"
          opacity={0}
          position="absolute"
          right="-8px"
          top="-8px"
          transition="all 0.2s"
          zIndex={1}
        >
          <Box
            _hover={{
              '& .edit-button': {
                width: 'auto',
                paddingRight: 2,
              },
              '& .edit-label': {
                opacity: 1,
                maxWidth: '40px',
              },
              cursor: 'pointer',
            }}
            position="relative"
          >
            <Flex
              alignItems="center"
              bg="#2B2B2B"
              borderRadius="full"
              boxShadow="sm"
              className="edit-button"
              overflow="hidden"
              p={1}
              transition="all 0.2s"
              width="24px"
            >
              <Box
                alignItems="center"
                borderRadius="full"
                display="flex"
                justifyContent="center"
                minWidth="16px"
              >
                <Icon as={PencilIcon} boxSize={3} color="white" />
              </Box>
              <Text
                className="edit-label"
                color="white"
                fontSize="xs"
                fontWeight="medium"
                maxWidth={0}
                ml={1}
                opacity={0}
                overflow="hidden"
                transition="all 0.2s"
                whiteSpace="nowrap"
              >
                Edit
              </Text>
            </Flex>
          </Box>
        </Box>
      )}
    </ModalTrigger>
  );
};

type AdjustCompetitivenessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialCompetitiveness: CompetitivenessAdjustmentInput;
  onSubmit: (values: AdjustCompetitivenessFormValues) => Promise<void>;
};

type AdjustCompetitivenessFormValues = CompetitivenessAdjustmentInput;

const AdjustCompetitivenessModal = ({
  isOpen,
  onClose,
  initialCompetitiveness,
  onSubmit,
}: AdjustCompetitivenessModalProps) => {
  // Order competitiveness options from least competitive to most competitive
  const competitivenessOptions = Object.keys(COMPETITIVENESS_ORDER)
    .filter((level) => level !== CompetitivenessLevel.UNKNOWN)
    .map((level: CompetitivenessLevel) => ({
      label: (
        <Flex alignItems="center" gap={1}>
          <CompetitivenessLabel level={level} />
        </Flex>
      ),
      value: level,
    }));

  const handleSubmit = async (values: AdjustCompetitivenessFormValues) => {
    await onSubmit(values);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Market Competitiveness</ModalHeader>
        <ModalCloseButton />
        <ModalBody px={6}>
          <Formik
            initialValues={initialCompetitiveness}
            onSubmit={handleSubmit}
          >
            {(props) => {
              const { values, setFieldValue, isSubmitting } = props;

              return (
                <ModalBody p={0}>
                  <VStack as={Form} direction="column" gap={6}>
                    <FormControl key="adjustedLevel">
                      <FormLabel fontSize="md" fontWeight="medium">
                        Competitiveness
                      </FormLabel>
                      <Select
                        defaultValue={competitivenessOptions.find(
                          (option) => option.value === values.adjustedLevel
                        )}
                        menuPortalTarget={document.body}
                        options={competitivenessOptions}
                        placeholder="Please select"
                        size="md"
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                        onChange={(option) => {
                          if (option) {
                            void setFieldValue('adjustedLevel', option.value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormControl key="note">
                      <FormLabel fontSize="md" fontWeight="medium">
                        Note
                      </FormLabel>
                      <Textarea
                        defaultValue={values.reason}
                        id="reason"
                        name="reason"
                        placeholder="Enter reason for competitiveness adjustment"
                        rows={3}
                        onChange={(e) => {
                          void setFieldValue('reason', e.target.value);
                        }}
                      />
                    </FormControl>
                    <Flex gap={2} justifyContent="flex-end" mt={4} w="100%">
                      <Button
                        h={8}
                        size="sm"
                        variant="outline"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                      <Button
                        h={8}
                        isLoading={isSubmitting}
                        size="sm"
                        type="submit"
                      >
                        Save
                      </Button>
                    </Flex>
                  </VStack>
                </ModalBody>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
